import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Maximize2 } from 'react-feather';
import styles from './JobEditorModal.module.css';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import ExpandedContentModal from '../../components/Modals/ExpandedContentModal';
import { parseSalaryRange } from '../../utils/salaryUtils';
import {
  selectUserRole,
  selectOutlets,
  selectOrganizationName,
} from '../../store/selectors/authSelectors';
import { DashboardJob } from './types';

interface JobEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobToEdit: DashboardJob | null;
  onSave: (data: any) => void;
  isSubmitting: boolean;
}

interface FormData {
  title: string;
  description: string;
  minSalary: string;
  maxSalary: string;
  expectedHires: string;
  tentative_joining: string;
  outlet_id: string;
}

interface FormErrors {
  minSalary?: string;
  maxSalary?: string;
  outlet?: string;
}

const JobEditorModal: React.FC<JobEditorModalProps> = ({ isOpen, onClose, jobToEdit, onSave, isSubmitting }) => {
  // Form state
  const [formData, setFormData] = useState<FormData>({
    title: '',
    description: '',
    minSalary: '',
    maxSalary: '',
    expectedHires: '',
    tentative_joining: '',
    outlet_id: '',
  });
  const [initialState, setInitialState] = useState<FormData>({
    title: '',
    description: '',
    minSalary: '',
    maxSalary: '',
    expectedHires: '',
    tentative_joining: '',
    outlet_id: '',
  });
  const [isDirty, setIsDirty] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [isExpanded, setIsExpanded] = useState(false);

  // RBAC state from Redux
  const userRole = useSelector(selectUserRole);
  const outlets = useSelector(selectOutlets);
  const organizationName = useSelector(selectOrganizationName);
  const [selectedOutlet, setSelectedOutlet] = useState<string | null>(null);

  // Load form data when modal opens or jobToEdit changes
  useEffect(() => {
    if (isOpen) {
      let dataToLoad: FormData = {
        title: '',
        description: '',
        minSalary: '',
        maxSalary: '',
        expectedHires: '',
        tentative_joining: '',
        outlet_id: '',
      };

      // If jobToEdit exists, populate from full job object
      if (jobToEdit) {
        dataToLoad = {
          title: jobToEdit.title || '',
          description: jobToEdit.job_description || '',
          expectedHires: jobToEdit.expected_hires?.toString() || '',
          tentative_joining: jobToEdit.tentative_joining || '',
          outlet_id: jobToEdit.outlet_id || '',
          minSalary: '',
          maxSalary: '',
        };

        // Parse salary_range string to extract min/max
        if (jobToEdit.salary_range) {
          const parsed = parseSalaryRange(jobToEdit.salary_range);
          if (parsed) {
            dataToLoad.minSalary = parsed.min.toString();
            dataToLoad.maxSalary = parsed.max.toString();
          }
        }
      }

      setFormData(dataToLoad);
      setInitialState(dataToLoad);
      if (jobToEdit && jobToEdit.outlet_id) {
        setSelectedOutlet(jobToEdit.outlet_id);
      }
    }
  }, [jobToEdit, isOpen]);

  // Set selected outlet based on role and edit mode
  useEffect(() => {
    if (!isOpen || !userRole || !outlets || outlets.length === 0) {
      return;
    }

    // Auto-populate outlet_id for OUTLET_MANAGER and OUTLET_POC in create mode
    if (
      !jobToEdit &&
      (userRole === 'OUTLET_MANAGER' || userRole === 'OUTLET_POC') &&
      outlets.length > 0
    ) {
      const outletId = outlets[0].outlet_id;
      setSelectedOutlet(outletId);
      setFormData((prev) => ({ ...prev, outlet_id: outletId }));
    }
    // For edit mode, use jobToEdit.outlet_id
    else if (jobToEdit && jobToEdit.outlet_id) {
      setSelectedOutlet(jobToEdit.outlet_id);
    }
  }, [isOpen, userRole, outlets, jobToEdit]);

  // MODIFIED: This effect runs on EVERY change to the form data
  useEffect(() => {
    if (!isOpen) return; // Don't run comparison logic if modal is closed
    // Compare the stringified versions of the objects to see if they've changed
    const hasChanged = JSON.stringify(formData) !== JSON.stringify(initialState);
    setIsDirty(hasChanged);
  }, [formData, initialState, isOpen]);

  if (!isOpen) return null;

  const isEditMode = !!jobToEdit;

  const handleOutletChange = (outletId: string) => {
    setSelectedOutlet(outletId);
    setFormData({ ...formData, outlet_id: outletId });
  };

  const validateForm = () => {
    const newErrors: FormErrors = {};

    // Salary validation
    const min = parseInt(formData.minSalary);
    const max = parseInt(formData.maxSalary);
    if (min < 0) newErrors.minSalary = 'Salary must be positive';
    if (max < 0) newErrors.maxSalary = 'Salary must be positive';
    if (min > max) newErrors.maxSalary = 'Max salary must be greater than min salary';

    // Outlet validation
    if (!formData.outlet_id && !selectedOutlet) {
      newErrors.outlet = 'Please select an outlet';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSave(formData);
      // Reset form state
      setFormData({
        title: '',
        description: '',
        minSalary: '',
        maxSalary: '',
        expectedHires: '',
        tentative_joining: '',
        outlet_id: '',
      });
      setInitialState({
        title: '',
        description: '',
        minSalary: '',
        maxSalary: '',
        expectedHires: '',
        tentative_joining: '',
        outlet_id: '',
      });
      setIsDirty(false);
      setErrors({});
    }
  };

  const handleSaveExpanded = (newContent: string) => {
    setFormData({ ...formData, description: newContent });
  };

  return (
    <>
      <div className={styles.modalOverlay} onClick={onClose}>
        <div
          className={`${styles.modalContent} ${styles.jobEditorModal}`}
          onClick={(e) => e.stopPropagation()}
        >
          <form onSubmit={handleSubmit}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle} data-testid="job-editor-modal-title">
                {isEditMode ? `Edit Job: ${initialState.title}` : 'Post a New Job'}
              </h2>
              <button type="button" className={styles.modalClose} onClick={onClose}>
                <h3>x</h3>
              </button>
            </div>

            <div className={styles.formContent}>
              {/* Company Field - Auto-filled */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Company (Auto-filled)</label>
                <div className={`${styles.formInput} ${styles.formInputDisabled}`}>
                  <span>{organizationName || '—'}</span>
                </div>
              </div>

              {/* Job Title */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Job Title*</label>
                <input
                  type="text"
                  className={styles.formInput}
                  value={formData.title || ''}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Enter job title"
                  disabled={isSubmitting}
                  required
                  data-testid="job-title-input"
                />
              </div>

              {/* Job Description */}
              <div className={styles.formGroup}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
                  <label className={styles.formLabel} style={{ marginBottom: 0 }}>Job Description*</label>
                  <button
                    type="button"
                    className={styles.expandButton}
                    onClick={() => setIsExpanded(true)}
                    title="Expand Editor"
                  >
                    <Maximize2 size={14} />
                  </button>
                </div>
                <textarea
                  className={styles.formTextarea}
                  value={formData.description || ''}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the role, responsibilities, and requirements"
                  rows={4}
                  disabled={isSubmitting}
                  required
                  data-testid="job-description-input"
                />
              </div>

              {/* Salary Range */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Salary Range (per month)*</label>
                <div className={styles.salaryRange}>
                  <input
                    type="number"
                    className={`${styles.formInput} ${styles.salaryInput}`}
                    value={formData.minSalary || ''}
                    onChange={(e) => setFormData({ ...formData, minSalary: e.target.value })}
                    placeholder="Minimum"
                    disabled={isSubmitting}
                    required
                    min="0"
                    data-testid="min-salary-input"
                  />
                  <input
                    type="number"
                    className={`${styles.formInput} ${styles.salaryInput}`}
                    value={formData.maxSalary || ''}
                    onChange={(e) => setFormData({ ...formData, maxSalary: e.target.value })}
                    placeholder="Maximum"
                    disabled={isSubmitting}
                    required
                    min="0"
                    data-testid="max-salary-input"
                  />
                </div>
                {(errors.minSalary || errors.maxSalary) && (
                  <div className={styles.errorMessage} data-testid="salary-error">{errors.minSalary || errors.maxSalary}</div>
                )}
              </div>

              {/* Expected Hires */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Expected Hires*</label>
                <input
                  type="number"
                  className={styles.formInput}
                  value={formData.expectedHires || ''}
                  onChange={(e) => setFormData({ ...formData, expectedHires: e.target.value })}
                  placeholder="Number of positions to fill"
                  disabled={isSubmitting}
                  required
                  data-testid="expected-hires-input"
                />
              </div>

              {/* Tentative Joining */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Tentative Joining*</label>
                <div className={styles.checkboxGroup}>
                  <input
                    type="checkbox"
                    id="immediate_joining"
                    className={styles.checkbox}
                    checked={formData.tentative_joining === 'Immediate'}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      setFormData({
                        ...formData,
                        tentative_joining: isChecked ? 'Immediate' : '',
                      });
                    }}
                    disabled={isSubmitting}
                    data-testid="tentative-joining-checkbox"
                  />
                  <label htmlFor="immediate_joining" className={styles.checkboxLabel}>
                    Immediate joining
                  </label>
                </div>

                {formData.tentative_joining !== 'Immediate' && (
                  <div style={{ marginTop: '8px' }}>
                    <input
                      type="date"
                      className={styles.formInput}
                      value={
                        formData.tentative_joining && formData.tentative_joining.includes('-')
                          ? formData.tentative_joining.split('-').length === 3 &&
                            formData.tentative_joining.split('-')[0].length === 4
                            ? formData.tentative_joining // Already YYYY-MM-DD
                            : formData.tentative_joining.split('-').reverse().join('-') // Convert DD-MM-YYYY to YYYY-MM-DD
                          : ''
                      }
                      onChange={(e) => {
                        const dateVal = e.target.value; // YYYY-MM-DD
                        if (dateVal) {
                          const formattedDate = dateVal.split('-').reverse().join('-'); // DD-MM-YYYY
                          setFormData({ ...formData, tentative_joining: formattedDate });
                        } else {
                          setFormData({ ...formData, tentative_joining: '' });
                        }
                      }}
                      min={new Date().toISOString().split('T')[0]}
                      disabled={isSubmitting}
                      required={formData.tentative_joining !== 'Immediate'}
                      data-testid="tentative-joining-date-input"
                    />
                  </div>
                )}
              </div>

              {/* Outlet Selection - RBAC based */}
              <div className={styles.formGroup}>
                <label className={styles.formLabel}>Outlet*</label>
                {!userRole || outlets.length === 0 ? (
                  <div className={styles.loadingMessage}>Loading outlets...</div>
                ) : userRole === 'ORG_ADMIN' ? (
                  <>
                    <select
                      className={styles.formInput}
                      value={selectedOutlet || ''}
                      onChange={(e) => handleOutletChange(e.target.value)}
                      disabled={isSubmitting}
                      required
                      data-testid="outlet-select"
                      >
                        <option value="">Select an outlet</option>
                        {outlets.map((outlet) => (
                          <option key={outlet.outlet_id} value={outlet.outlet_id}>
                            {outlet.outlet_name}
                          </option>
                        ))}
                      </select>
                    {errors.outlet && <div className={styles.errorMessage} data-testid="outlet-error">{errors.outlet}</div>}
                  </>
                ) : (userRole === 'OUTLET_MANAGER' || userRole === 'OUTLET_POC') &&
                  outlets.length > 0 ? (
                  <div className={`${styles.formInput} ${styles.formInputDisabled}`}>
                    <span>{outlets[0].outlet_name}</span>
                  </div>
                ) : (
                  <div className={styles.errorText}>Unable to load outlet information</div>
                )}
              </div>
            </div>

            <div className={styles.modalFooter}>
              <button
                type="button"
                className={styles.buttonSecondary}
                onClick={onClose}
                disabled={isSubmitting}
                data-testid="cancel-button"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={styles.buttonPrimary}
                disabled={(isEditMode && !isDirty) || isSubmitting}
                data-testid="submit-button"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size="small" />
                    <span style={{ marginLeft: '8px' }}>
                      {isEditMode ? 'Saving...' : 'Posting...'}
                    </span>
                  </>
                ) : isEditMode ? (
                  'Save Changes'
                ) : (
                  'Post Job'
                )}
              </button>
            </div>
          </form>
        </div>

        <ExpandedContentModal
          isOpen={isExpanded}
          onClose={() => setIsExpanded(false)}
          title="Job Description"
          content={formData.description}
          onSave={handleSaveExpanded}
          placeholder="Describe the role, responsibilities, and requirements..."
        />
      </div>
    </>
  );
};

export default JobEditorModal;
